import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import './FAQManagement.css';
import logger from './utils/logger';

function FAQManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, id: null, question: '', answer: '' });
  const [formData, setFormData] = useState({ question: '', answer: '' });
  const [submitting, setSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/faq', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      logger.apiError('GET', '/api/faq', err);
      setError('FAQ 목록을 불러오지 못했습니다.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const openAddModal = () => {
    setFormData({ question: '', answer: '' });
    setModalOpen(true);
  };

  const closeAddModal = () => {
    setModalOpen(false);
    setFormData({ question: '', answer: '' });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!formData.question.trim() || !formData.answer.trim()) {
      alert('질문과 답변을 모두 입력해주세요.');
      return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.post('/api/faq', {
        question: formData.question.trim(),
        answer: formData.answer.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      closeAddModal();
      await fetchFaqs();
    } catch (err) {
      logger.apiError('POST', '/api/faq', err);
      alert(err?.response?.data?.error || 'FAQ 등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (item) => {
    setEditModal({
      open: true,
      id: item.id,
      question: item.question || '',
      answer: item.answer || ''
    });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, id: null, question: '', answer: '' });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editModal.question.trim() || !editModal.answer.trim()) {
      alert('질문과 답변을 모두 입력해주세요.');
      return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.put(`/api/faq/${editModal.id}`, {
        question: editModal.question.trim(),
        answer: editModal.answer.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      closeEditModal();
      setExpandedId(null);
      await fetchFaqs();
    } catch (err) {
      logger.apiError('PUT', '/api/faq/:id', err);
      alert(err?.response?.data?.error || 'FAQ 수정에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.question}" FAQ를 삭제할까요?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/faq/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (expandedId === item.id) setExpandedId(null);
      await fetchFaqs();
    } catch (err) {
      logger.apiError('DELETE', '/api/faq/:id', err);
      alert(err?.response?.data?.error || 'FAQ 삭제에 실패했습니다.');
    }
  };

  const handleCopyAnswer = (id, answer) => {
    navigator.clipboard.writeText(answer).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => {
      alert('복사에 실패했습니다.');
    });
  };

  return (
    <div className="faq-management-container">
      <div className="section-card">
        <div className="section-header">
          <div>
            <h1 className="section-title">FAQ 관리</h1>
            <p className="section-subtitle">자주 묻는 질문을 등록하고 복사·붙여넣기로 활용하세요.</p>
          </div>
          <button className="create-user-btn" onClick={openAddModal}>
            + FAQ 등록
          </button>
        </div>

        {loading ? (
          <div className="loading">불러오는 중...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : items.length === 0 ? (
          <div className="no-data">등록된 FAQ가 없습니다. FAQ 등록 버튼으로 추가해주세요.</div>
        ) : (
          <div className="faq-accordion-list">
            {items.map((item) => (
              <div
                key={item.id}
                className={`faq-accordion-item ${expandedId === item.id ? 'expanded' : ''}`}
              >
                <div
                  className="faq-question-row"
                  onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                >
                  <span className="faq-question-text">{item.question}</span>
                  <span className="faq-accordion-icon">{expandedId === item.id ? '−' : '+'}</span>
                </div>
                {expandedId === item.id && (
                  <div className="faq-answer-area">
                    <div className="faq-answer-content">
                      <pre className="faq-answer-pre">{item.answer}</pre>
                    </div>
                    <div className="faq-answer-actions">
                      <button
                        type="button"
                        className="edit-user-btn"
                        onClick={(e) => { e.stopPropagation(); openEditModal(item); }}
                      >
                        수정
                      </button>
                      <button
                        type="button"
                        className="delete-user-btn"
                        onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                      >
                        삭제
                      </button>
                        <button
                        type="button"
                        className="faq-copy-btn"
                        onClick={(e) => { e.stopPropagation(); handleCopyAnswer(item.id, item.answer); }}
                      >
                        {copiedId === item.id ? '✓ 복사됨' : '답변 복사'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FAQ 등록 모달 */}
      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>FAQ 등록</h3>
              <button className="modal-close" onClick={closeAddModal}>×</button>
            </div>
            <form onSubmit={handleAddSubmit}>
              <div className="modal-content">
                <div className="form-group">
                  <label htmlFor="addQuestion">질문 *</label>
                  <input
                    id="addQuestion"
                    type="text"
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    placeholder="질문을 입력하세요"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="addAnswer">답변 *</label>
                  <textarea
                    id="addAnswer"
                    value={formData.answer}
                    onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                    placeholder="답변을 입력하세요 (복사 붙여넣기로 활용)"
                    rows={8}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={closeAddModal} disabled={submitting}>취소</button>
                <button type="submit" className="confirm-btn" disabled={submitting}>
                  {submitting ? '등록 중...' : '등록'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FAQ 수정 모달 */}
      {editModal.open && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>FAQ 수정</h3>
              <button className="modal-close" onClick={closeEditModal}>×</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-content">
                <div className="form-group">
                  <label htmlFor="editQuestion">질문 *</label>
                  <input
                    id="editQuestion"
                    type="text"
                    value={editModal.question}
                    onChange={(e) => setEditModal({ ...editModal, question: e.target.value })}
                    placeholder="질문을 입력하세요"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="editAnswer">답변 *</label>
                  <textarea
                    id="editAnswer"
                    value={editModal.answer}
                    onChange={(e) => setEditModal({ ...editModal, answer: e.target.value })}
                    placeholder="답변을 입력하세요"
                    rows={8}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={closeEditModal} disabled={submitting}>취소</button>
                <button type="submit" className="confirm-btn" disabled={submitting}>
                  {submitting ? '수정 중...' : '수정'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FAQManagement;
